// ============================================================================
// VIEWS API — React Query hooks for View CRUD and widget management
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import type {
  View,
  SharedView,
  ViewPlacement,
  CreateViewInput,
  UpdateViewInput,
  AddWidgetInput,
  UpdateWidgetLayoutInput,
} from '@/types/views'

const VIEWS_KEY = ['views']

export function useViews() {
  return useQuery({
    queryKey: VIEWS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<View[]>('/views/')
      return data
    },
  })
}

export function useView(id: number | undefined) {
  return useQuery({
    queryKey: ['views', id],
    queryFn: async () => {
      const { data } = await apiClient.get<View>(`/views/${id}/`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateViewInput) => {
      const { data } = await apiClient.post<View>('/views/', input)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
    },
  })
}

export function useUpdateView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateViewInput & { id: number }) => {
      const { data } = await apiClient.patch<View>(`/views/${id}/`, input)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
      queryClient.setQueryData(['views', data.id], data)
    },
  })
}

export function useDeleteView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/views/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
    },
  })
}

export function useAddWidgetToView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewId, ...input }: AddWidgetInput & { viewId: number }) => {
      const { data } = await apiClient.post<ViewPlacement>(`/views/${viewId}/widgets/`, input)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['views', variables.viewId] })
    },
  })
}

export function useUpdateWidgetLayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      viewId,
      widgetId,
      layout,
    }: UpdateWidgetLayoutInput & { viewId: number; widgetId: number }) => {
      const { data } = await apiClient.patch<ViewPlacement>(
        `/views/${viewId}/widgets/${widgetId}/`,
        { layout }
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['views', variables.viewId] })
    },
  })
}

export function useRemoveWidgetFromView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewId, widgetId }: { viewId: number; widgetId: number }) => {
      await apiClient.delete(`/views/${viewId}/widgets/${widgetId}/remove/`)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['views', variables.viewId] })
    },
  })
}

export function useDuplicateView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<View>(`/views/${id}/duplicate/`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
    },
  })
}

export function useShareView() {
  const queryClient = useQueryClient()
  return useMutation({
    // The endpoint returns only {share_token}, NOT a View — so there is nothing
    // to seed the cache with here. The dialog reads share_token off the view
    // itself, which the invalidate below refetches (prefix-matches the detail
    // key ['views', id] too).
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<{ share_token: string }>(`/views/${id}/share/`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
    },
  })
}

export function useRevokeShareView() {
  const queryClient = useQueryClient()
  return useMutation({
    // Kills every link handed out for this lens. Sharing again mints a NEW
    // token; the old URL stays dead (410), it does not come back.
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<{ revoked: number }>(`/views/${id}/revoke-share/`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
    },
  })
}

export function useSharedView(token: string | undefined) {
  return useQuery({
    queryKey: ['shared-view', token],
    queryFn: async () => {
      const { data } = await apiClient.get<SharedView>(`/views/shared/${token}/`)
      return data
    },
    enabled: !!token,
  })
}

export function useCloneSharedView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await apiClient.post<View>(`/views/shared/${token}/clone/`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEWS_KEY })
    },
  })
}
